import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tabs, Table, Button, Select, DatePicker, message, Input } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { useSize, useDebounce } from 'ahooks';
import { useDispatch } from "react-redux";
import { setBreadcrumbList } from "../../store/menuInfo/menuInfo";
import apis from '../../apis';
import './index.scss';

const { RangePicker } = DatePicker;

const TocCreditRecord = () => {
    const dispatch = useDispatch();
    const [mode, setMode] = useState('enterprise'); // 'enterprise' | 'personal'
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [productList, setProductList] = useState([]);
    const [selectedProductId, setSelectedProductId] = useState(null);

    const refTable = useRef(null);
    const refHeader = useRef(null);
    const size = useSize(refTable);
    const headerSize = useSize(refHeader);

    // Filters
    const [companyList, setCompanyList] = useState([]);
    const [userList, setUserList] = useState([]);
    const [filterCompanyId, setFilterCompanyId] = useState(null);
    const [filterUserId, setFilterUserId] = useState(null);
    const [filterTimeRange, setFilterTimeRange] = useState(null);
    const [filterType, setFilterType] = useState(undefined);
    const [filterSummary, setFilterSummary] = useState('');
    const [companySearchLoading, setCompanySearchLoading] = useState(false);
    const [userSearchLoading, setUserSearchLoading] = useState(false);

    const debouncedSummary = useDebounce(filterSummary, { wait: 500 });

    // Stats
    const [stats, setStats] = useState({
        subscription_credits: 0,
        topup_credits: 0,
        expired_credits: 0,
        consumed_credits: 0,
        remaining_valid_credits: 0
    });

    const isEnterprise = mode === 'enterprise';

    // 获取产品列表
    useEffect(() => {
        apis.getProductList("TOC").then((res) => {
            if (res.code === 200) {
                setProductList(res.data || []);
                // 产品不设置默认值，由用户手动选择
            }
        });
    }, []);

    // Search helpers
    const fetchCompanyList = (value) => {
        if (!value) {
            setCompanyList([]);
            return;
        }
        setCompanySearchLoading(true);
        apis.getTocCompanyList({ 
            keyword: value,
            page: 1,
            page_size: 50
        }).then((res) => {
            setCompanySearchLoading(false);
            if (res.code === 200) {
                setCompanyList(res.data?.data || []);
            }
        }).catch(() => {
            setCompanySearchLoading(false);
            setCompanyList([]);
        });
    };

    const fetchUserList = (value) => {
        if (!value) {
            setUserList([]);
            return;
        }
        setUserSearchLoading(true);
        apis.searchTocUserByPhone({
            phone: value,
        }).then((res) => {
            setUserSearchLoading(false);
            if (res.code === 200) {
                setUserList(res.data || []);
            }
        }).catch(() => {
            setUserSearchLoading(false);
            setUserList([]);
        });
    };

    const debounceCompanyFetcher = useMemo(() => {
        return debounce(fetchCompanyList, 500);
    }, []);

    const debounceUserFetcher = useMemo(() => {
        return debounce(fetchUserList, 500);
    }, []);

    const handleSearchCompany = (value) => {
        debounceCompanyFetcher(value);
    };
    
    const handleSearchUser = (value) => {
        debounceUserFetcher(value);
    };

    // Fetch Data
    const getDataFunc = useCallback(() => {
        setLoading(true);
        const isEnt = mode === 'enterprise';
        const params = {
            account_type: isEnt ? 'SEAT' : 'PERSONAL',
            prd_id: selectedProductId,
            toc_co_id: isEnt ? filterCompanyId : undefined,
            user_id: !isEnt ? filterUserId : undefined,
            credit_chg_type_code: filterType,
            summary: debouncedSummary,
            start_time: filterTimeRange?.[0] ? dayjs(filterTimeRange[0]).format("YYYY-MM-DD 00:00:00") : undefined,
            end_time: filterTimeRange?.[1] ? dayjs(filterTimeRange[1]).format("YYYY-MM-DD 23:59:59") : undefined,
            page: page,
            page_size: pageSize,
        };

        // Remove undefined params
        Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

        apis.getTocCreditRecordList(params).then((res) => {
            if (res.code === 200) {
                setData(res.data?.items || res.data?.data || []);
                setTotal(res.data?.total || 0);
                if (res.data?.statistics) {
                    setStats({
                        subscription_credits: res.data.statistics.subscription_credits || 0,
                        topup_credits: res.data.statistics.topup_credits || 0,
                        expired_credits: res.data.statistics.expired_credits || 0,
                        consumed_credits: res.data.statistics.consumed_credits || 0,
                        remaining_valid_credits: res.data.statistics.remaining_valid_credits || 0
                    });
                }
            } else {
                setData([]);
                setTotal(0);
                message.error(res.message);
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    }, [mode, filterCompanyId, filterUserId, filterType, debouncedSummary, filterTimeRange, page, pageSize, selectedProductId]);

    useEffect(() => {
        dispatch(setBreadcrumbList([{ title: 'TOC积分记录' }]));
    }, [dispatch]);

    useEffect(() => {
        setPage(1);
    }, [mode, filterCompanyId, filterUserId, filterType, debouncedSummary, filterTimeRange, selectedProductId]);

    useEffect(() => {
        getDataFunc();
    }, [mode, filterCompanyId, filterUserId, filterType, debouncedSummary, filterTimeRange, page, pageSize, getDataFunc]);

    const handleDownload = () => {
        const params = {
            account_type: isEnterprise ? 'SEAT' : 'PERSONAL',
            prd_id: selectedProductId,
            toc_co_id: isEnterprise ? filterCompanyId : undefined,
            user_id: !isEnterprise ? filterUserId : undefined,
            credit_chg_type_code: filterType,
            summary: filterSummary || undefined,
            start_time: filterTimeRange?.[0] ? dayjs(filterTimeRange[0]).format("YYYY-MM-DD 00:00:00") : undefined,
            end_time: filterTimeRange?.[1] ? dayjs(filterTimeRange[1]).format("YYYY-MM-DD 23:59:59") : undefined,
        };
        
        apis.downloadTocCreditRecordList(params).then((res) => {
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);

            let fileName = `TOC积分记录_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`;
            const contentDisposition = res.headers["content-disposition"];
            if (contentDisposition) {
                // 先尝试匹配 filename*=UTF-8'' 格式 (RFC 5987)
                const filenameStarRegex = /filename\*=(?:UTF-8'')?([^;\n]*)/i;
                const starMatch = filenameStarRegex.exec(contentDisposition);
                if (starMatch && starMatch[1]) {
                    const encodedFileName = starMatch[1].replace(/^UTF-8''/, '').replace(/['"]/g, '');
                    try {
                        fileName = decodeURIComponent(encodedFileName);
                    } catch {
                        fileName = encodedFileName;
                    }
                } else {
                    // 回退匹配普通 filename= 格式
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(contentDisposition);
                    if (matches != null && matches[1]) {
                        fileName = decodeURIComponent(matches[1].replace(/['"]/g, ''));
                    }
                }
            }

            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(link.href);
        }).catch(() => {
            message.error('下载失败');
        });
    };

    const columns = [
        ...(isEnterprise ? [{
            title: "企业(编号/名称)",
            key: "company",
            align: "center",
            width: 200,
            render: (_, record) => record.enterprise_display || `${record.toc_co_code || '-'} / ${record.toc_co_name || '-'}`
        },
        {
            title: "企业所有者",
            key: "owner",
            align: "center",
            width: 150,
            render: (_, record) => record.owner_user_display || '-'
        },
        {
            title: "席位",
            key: "seat",
            align: "center",
            width: 100,
            render: (_, record) => record.seat_display || '-'
        },
        {
            title: "席位使用者",
            key: "seatUser",
            align: "center",
            width: 150,
            render: (_, record) => record.seat_user_display || '-'
        }] : [{
            title: "用户(手机/昵称)",
            key: "user",
            align: "center",
            width: 180,
            render: (_, record) => record.user_display || '-'
        }]),
        {
            title: "产品",
            dataIndex: "prd_name",
            key: "prd_name",
            align: "center",
            width: 120,
        },
        {
            title: "积分变动类型",
            dataIndex: "credit_chg_type_name",
            key: "credit_chg_type_name",
            align: "center",
            width: 120,
        },
        {
            title: "变动时间",
            dataIndex: "credit_chg_time",
            key: "credit_chg_time",
            align: "center",
            width: 180,
            render: (text) => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        {
            title: "摘要",
            dataIndex: "summary",
            key: "summary",
            align: "center",
            width: 200,
            // ellipsis: true,
        },
        {
            title: "备注",
            dataIndex: "remark",
            key: "remark",
            align: "center",
            width: 200,
            // ellipsis: true,
        },
        {
            title: "积分",
            dataIndex: "credits",
            key: "credits",
            align: "center",
            width: 120,
            render: (text, record) => {
                const code = record.credit_chg_type_code;
                const num = parseInt(text) || 0;
                if (code === 'SUBSCRIPTION_CREDIT' || code === 'TOPUP_CREDIT') {
                    return <span style={{ color: '#52c41a' }}>+{Math.abs(num).toLocaleString()}</span>;
                }
                if (code === 'CONSUMPTION' || code === 'EXPIRATION' || code === 'TOPUP_CANCEL') {
                    return <span style={{ color: '#ff4d4f' }}>-{Math.abs(num).toLocaleString()}</span>;
                }
                return <span>{num.toLocaleString()}</span>;
            }
        },
    ];

    return (
        <div className="equityRecordContent" ref={refTable}>
            <div ref={refHeader}>
                <div className="filterItemsBox">
                    <Tabs
                        activeKey={mode}
                        onChange={(key) => {
                            setMode(key);
                            setPage(1);
                            setFilterCompanyId(null);
                            setFilterUserId(null);
                            setFilterTimeRange(null);
                            setFilterType(undefined);
                            setFilterSummary('');
                            setCompanyList([]);
                            setUserList([]);
                        }}
                        className="modeTabs"
                        items={[
                            { key: 'enterprise', label: '企业' },
                            { key: 'personal', label: '个人' }
                        ]}
                        style={{ width: '100%' }}
                    />

                    <div className="filterItemsContent">
                        <div className="filterItem">
                            <span className="filterItemSpan">产品</span>
                            <Select
                                placeholder="请选择产品"
                                value={selectedProductId}
                                onChange={(val) => {
                                    setSelectedProductId(val);
                                    setPage(1);
                                }}
                                allowClear
                                onClear={() => {
                                    setSelectedProductId(null);
                                    setPage(1);
                                }}
                                style={{ width: 180 }}
                                options={productList.map((p) => ({
                                    value: p.prd_id,
                                    label: p.prd_name,
                                }))}
                            />
                        </div>

                        {isEnterprise ? (
                            <div className="filterItem">
                                <span className="filterItemSpan">企业</span>
                                <Select
                                    showSearch
                                    placeholder="输入所有者手机号或企业名称、编号搜索"
                                    value={filterCompanyId}
                                    onChange={(val) => {
                                        setFilterCompanyId(val);
                                        setPage(1);
                                    }}
                                    onSearch={handleSearchCompany}
                                    filterOption={false}
                                    notFoundContent={null}
                                    loading={companySearchLoading}
                                    style={{ width: 240 }}
                                    popupMatchSelectWidth={false}
                                    allowClear
                                    onClear={() => {
                                        setCompanyList([]);
                                        setFilterCompanyId(null);
                                        setPage(1);
                                    }}
                                    labelRender={
                                        (option) => {
                                            const info = JSON.parse(option.label || '');
                                            return (
                                                <div className="PersonAndCompanySelectItem3">
                                                    <a className="nameBox" title={info.toc_co_name}>
                                                        <div className='nameBoxContent' style={{ fontWeight: 'unset' }}>{info.toc_co_name}</div>
                                                    </a>
                                                    <span className="codeBox">企业编号:{info.toc_co_code}</span>
                                                </div>
                                            )
                                        }
                                    }
                                    optionRender={(option) => {
                                        const info = JSON.parse(option.label || '');
                                        return (
                                            <div>
                                                <div className="PersonAndCompanySelectItem3">
                                                    <a className="nameBox" title={info.toc_co_name}>
                                                        <div className='nameBoxContent'>{info.toc_co_name}</div>
                                                    </a>
                                                    <span className="codeBox">企业编号:{info.toc_co_code}</span>
                                                </div>
                                                <div className='ownerBox'>所有者:{info.user_phone}/{info.user_name}</div>
                                            </div>
                                        )
                                    }}
                                    options={companyList.map(c => ({ 
                                        value: c.toc_co_id, 
                                        label: JSON.stringify(c)
                                    }))}
                                />
                            </div>
                        ) : (
                            <div className="filterItem">
                                <span className="filterItemSpan">用户</span>
                                <Select
                                    showSearch
                                    placeholder="请输入手机号查询"
                                    value={filterUserId}
                                    onChange={(val) => {
                                        setFilterUserId(val);
                                        setPage(1);
                                    }}
                                    onSearch={handleSearchUser}
                                    filterOption={false}
                                    style={{ width: 200 }}
                                    allowClear
                                    loading={userSearchLoading}
                                    notFoundContent={null}
                                    onClear={() => {
                                        setUserList([]);
                                        setFilterUserId(null);
                                        setPage(1);
                                    }}
                                    options={userList.map(u => ({ 
                                        value: u.user_id, 
                                        label: (
                                            <div className="PersonAndCompanySelectItem2">
                                                <span className="mobileBox" title={u.mobile_no || u.phone}>
                                                    {u.mobile_no || u.phone}
                                                </span>
                                                <span className="nameBox" title={u.user_nickname || u.nickname || u.show_name} style={{ textAlign: "right" }}>
                                                    {u.user_nickname || u.nickname || u.show_name}
                                                </span>
                                            </div>
                                        )
                                    }))}
                                />
                            </div>
                        )}
                        
                        <div className="filterItem">
                            <span className="filterItemSpan">变动时间</span>
                            <RangePicker
                                value={filterTimeRange}
                                onChange={(dates) => {
                                    setFilterTimeRange(dates);
                                    setPage(1);
                                }}
                                style={{ width: 260 }}
                                allowClear
                                placeholder={['开始日期', '结束日期']}
                            />
                        </div>
                        <div className="filterItem">
                            <span className="filterItemSpan">变动类型</span>
                            <Select
                                placeholder="全部"
                                style={{ width: 120 }}
                                allowClear
                                value={filterType}
                                onChange={(val) => {
                                    setFilterType(val);
                                    setPage(1);
                                }}
                                options={[
                                    { value: 'SUBSCRIPTION_CREDIT', label: '积分赠送' },
                                    { value: 'TOPUP_CREDIT', label: '积分充值' },
                                    { value: 'CONSUMPTION', label: '积分消费' },
                                    { value: 'EXPIRATION', label: '积分失效' },
                                    // { value: 'TOPUP_CANCEL', label: '充值撤销' }
                                ]}
                            />
                        </div>
                        <div className="filterItem">
                            <span className="filterItemSpan">摘要</span>
                            <Input
                                placeholder="请输入摘要"
                                value={filterSummary}
                                onChange={(e) => setFilterSummary(e.target.value)}
                                style={{ width: 150 }}
                                allowClear
                                onClear={() => {
                                    setFilterSummary('');
                                    setPage(1);
                                }}
                            />
                        </div>
                        
                        <div style={{ marginLeft: "auto" }}>
                            <Button type="primary" className="btn" icon={<DownloadOutlined />} onClick={handleDownload}>
                                下载
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="equityStatsBox">
                    <div className="statItem">
                        <span className="statLabel">累计赠送积分:</span>
                        <span className="statValue" style={{ color: "#52c41a" }}>{stats.subscription_credits.toLocaleString()}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">累计充值积分:</span>
                        <span className="statValue" style={{ color: "#006AB2" }}>{stats.topup_credits.toLocaleString()}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">累计失效积分:</span>
                        <span className="statValue" style={{ color: "#FE545F" }}>{stats.expired_credits.toLocaleString()}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">累计消费积分:</span>
                        <span className="statValue" style={{ color: "#FE545F" }}>{stats.consumed_credits.toLocaleString()}</span>
                    </div>
                    <div className="statItem">
                        <span className="statLabel">剩余有效积分:</span>
                        <span className="statValue" style={{ color: "#61D4AD" }}>{stats.remaining_valid_credits.toLocaleString()}</span>
                    </div>
                </div>
            </div>

            <div className="tableContent">
                <Table
                    className="myFormTable"
                    dataSource={data}
                    columns={columns}
                    rowKey="credit_rec_id"
                    loading={loading}
                    bordered={true}
                    scroll={{ y: size?.height ? size.height - (headerSize?.height || 0) - 180 + 'px' : undefined }}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`,
                        onChange: (p, s) => {
                            setPage(p);
                            setPageSize(s);
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default TocCreditRecord;
